import React, { Component } from 'react';
import WidgetFilterBox from './WidgetFilterBox';
import { fetchApi } from '../../../utils/DatabaseHelper';
import { WIDGET_LIST, WIDGET_EXAMINATION } from '../../../utils/TableConstanten';
import { settingsStorage } from '../../../settings/storage';
import './ListWidget.scss';

class ListWidget extends Component {
  constructor(props) {
    super(props);
    
    // Verwende die lokalen Einstellungen aus der Widget-Konfiguration
    const localSettings = props.config?.localSettings || {};
    
    // Bereichsauswahl aus lokalen Einstellungen laden
    const initialPrimaryFilterValue = localSettings.selectedAreas || [];
    
    // Status-Filter und Top 5 Modus aus lokalen Einstellungen laden
    const initialStatusFilterValue = localSettings.statusFilter || '';
    const initialIsTop5Mode = localSettings.isTop5Mode !== undefined ? localSettings.isTop5Mode : initialPrimaryFilterValue.length === 0;

    this.state = {
      data: [],
      areaOptions: [], // Verfügbare Bereiche aus API
      loading: false,
      error: null,
      primaryFilterValue: initialPrimaryFilterValue, // Array für Mehrfachauswahl
      statusFilterValue: initialStatusFilterValue,   // Status-Filterwert
      isTop5Mode: initialIsTop5Mode // Top 5 Modus
    };
  }

  componentDidMount() {
    // Initiale Daten laden
    this.fetchAreaOptions();
    this.fetchData();
    
    // Event-Handler für das REFRESH_DASHBOARD-Event
    this.handleRefreshDashboard = () => {
      this.fetchData();
    };
    
    // Füge Event-Listener hinzu
    global.emitter.on('REFRESH_DASHBOARD', this.handleRefreshDashboard);
  }
  
  componentWillUnmount() {
    // Entferne Event-Listener
    global.emitter.off('REFRESH_DASHBOARD', this.handleRefreshDashboard);
  }

  componentDidUpdate(prevProps, prevState) {
    // Daten neu laden, wenn sich Filter ändern
    if (
      prevState.primaryFilterValue !== this.state.primaryFilterValue ||
      prevState.statusFilterValue !== this.state.statusFilterValue ||
      prevState.isTop5Mode !== this.state.isTop5Mode
    ) {
      this.fetchData();
      
      // Aktualisiere die lokalen Einstellungen im Widget
      if (this.props.onConfigChange) {
        const updatedConfig = {
          ...this.props.config,
          localSettings: {
            ...this.props.config.localSettings,
            selectedAreas: this.state.primaryFilterValue,
            statusFilter: this.state.statusFilterValue,
            isTop5Mode: this.state.isTop5Mode
          }
        };
        this.props.onConfigChange(updatedConfig);
        
        // Speichere die Änderungen direkt in der Datenbank
        settingsStorage.updateConfig(this.props.config.id, updatedConfig);
        settingsStorage.saveToBackend([updatedConfig]);
      }
    }
  }

  fetchAreaOptions = async () => {
    try {
      // Bestimmen der Datenquelle aus den Widget-Einstellungen
      const dataSource = this.props.config?.globalSettings?.dataSource || 'areas';
      
      // Abrufen der entsprechenden Daten basierend auf der Datenquelle
      let areaData;
      if (dataSource === 'examinations') {
        // Für Untersuchungen holen wir die Firmenkartei-Daten
        areaData = await fetchApi("firmenkartei");
      } else {
        // Standardmäßig Bereiche laden
        areaData = await fetchApi("zuordnung");
      }
      
      // Prüfen, ob areaData und areaData.data existieren
      if (areaData && Array.isArray(areaData.data)) {
      // Formatieren der Daten für die Verwendung im CustomSelect
      const formattedAreas = areaData.data.map(area => ({
        id: area.id,
        name: area.bezeichnung || area.name || `${area.bg_grundsatz} - ${area.titel}` // Je nach Datenquelle verwenden wir bezeichnung, name oder bg_grundsatz + titel
      }));
        
        this.setState({ areaOptions: formattedAreas });
        
        // Wenn noch keine Bereiche ausgewählt sind und der Top-5-Modus nicht aktiv ist, die ersten 5 Bereiche auswählen
        if (this.state.primaryFilterValue.length === 0 && !this.state.isTop5Mode) {
          const firstFiveAreas = formattedAreas.slice(0, 5).map(area => area.id);
          this.setState({ primaryFilterValue: firstFiveAreas });
        }
      } else {
        console.error('Unerwartetes Datenformat beim Laden der Bereichsdaten:', areaData);
        this.setState({ areaOptions: [] });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Bereichsdaten:', error);
      this.setState({ areaOptions: [] });
    }
  };

  fetchData = async () => {
    const { primaryFilterValue, statusFilterValue, isTop5Mode } = this.state;
    
    try {
      this.setState({ loading: true, error: null });
      
      // Bestimmen der Datenquelle aus den Widget-Einstellungen
      const dataSource = this.props.config?.globalSettings?.dataSource || 'areas';
      
      // Query-Parameter erstellen
      const query = {};
      
      // Status-Filter hinzufügen
      if (statusFilterValue) {
        query.status = statusFilterValue;
      }
      
      // Primärfilter-Werte hinzufügen (mehrere Werte möglich), außer im Top 5 Modus
      if (!isTop5Mode && primaryFilterValue && primaryFilterValue.length > 0) {
        query.areas = primaryFilterValue.join(',');
      }
      
      // Auswahl des richtigen API-Endpunkts basierend auf der Datenquelle
      let apiEndpoint;
      if (dataSource === 'examinations') {
        apiEndpoint = WIDGET_EXAMINATION;
      } else {
        apiEndpoint = WIDGET_LIST;
      }
      
      const fullData = await fetchApi(apiEndpoint, "", query);
      
      this.setState({ data: fullData, loading: false });
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      this.setState({ error: 'Daten konnten nicht geladen werden', loading: false });
    }
  };

  handlePrimaryFilterChange = (e) => {
    const selectedIdString = e.getAttribute("data-query");
    
    // Prüfen, ob "Top 5" ausgewählt wurde
    if (selectedIdString === 'top5') {
      // Top 5 Modus aktivieren und Bereichsauswahl leeren
      this.setState({ 
        primaryFilterValue: [], 
        isTop5Mode: true 
      });
      return;
    }
    
    const selectedId = parseInt(selectedIdString, 10);
    const newValues = [...this.state.primaryFilterValue];
    const index = newValues.indexOf(selectedId);
    
    if (index === -1) {
      // Hinzufügen, wenn nicht bereits ausgewählt
      newValues.push(selectedId);
    } else {
      // Entfernen, wenn bereits ausgewählt
      newValues.splice(index, 1);
    }
    
    // Begrenzen auf maximal 5 ausgewählte Elemente
    if (newValues.length <= 5) {
      this.setState({ 
        primaryFilterValue: newValues,
        isTop5Mode: false // Top 5 Modus deaktivieren, wenn ein spezifischer Bereich ausgewählt wird
      });
    }
  };

  handleStatusFilterChange = (e) => {
    const selectedStatus = e.getAttribute("data-query");
    this.setState({ statusFilterValue: selectedStatus });
  };

  getStatusColor = (status) => {
    switch (status) {
      case 'gültig':
        return '#4caf50'; // Grün
      case 'bald fällig':
        return '#ff9800'; // Orange
      case 'fällig':
        return '#f44336'; // Rot
      default:
        return '#9e9e9e'; // Grau
    }
  };

  render() {
    const { config } = this.props;
    const { data, areaOptions, loading, error, primaryFilterValue, statusFilterValue } = this.state;
    
      // Globale Einstellungen aus der Konfiguration holen
      const globalSettings = config.globalSettings || {};
      
      // Automatische Festlegung der Labels basierend auf der Datenquelle
      let primaryFilterLabel, columnHeaderLabel;
      
      if (globalSettings.primaryFilterLabel) {
        // Wenn explizit in den Einstellungen definiert, verwenden wir diesen Wert
        primaryFilterLabel = globalSettings.primaryFilterLabel;
      } else {
        // Automatische Festlegung basierend auf der Datenquelle
        primaryFilterLabel = globalSettings.dataSource === 'examinations' ? 'VORSORGEN:' : 'BEREICHE:';
      }
      
      if (globalSettings.columnHeaderLabel) {
        // Wenn explizit in den Einstellungen definiert, verwenden wir diesen Wert
        columnHeaderLabel = globalSettings.columnHeaderLabel;
      } else {
        // Automatische Festlegung basierend auf der Datenquelle
        columnHeaderLabel = globalSettings.dataSource === 'examinations' ? 'Vorsorge' : 'Bereich';
      }
    
    // Status-Filter-Optionen
    const statusOptions = [
      { name: "Alle", id: "" },
      { name: "Gültig", id: "gültig" },
      { name: "Bald fällig", id: "bald fällig" },
      { name: "Fällig", id: "fällig" }
    ];
    
    // Begrenze die angezeigten Daten auf 5 Einträge
    const displayData = data.slice(0, 5);

    return (
      <div className="list-widget">
        {/* Filterbereich */}
        <WidgetFilterBox
          areaFilter={areaOptions}
          onAreaChange={this.handlePrimaryFilterChange}
          areaFilterLabel={primaryFilterLabel}
          selectedAreas={primaryFilterValue}
          isTop5Mode={this.state.isTop5Mode}
          statusFilter={statusOptions}
          onStatusChange={this.handleStatusFilterChange}
          selectedStatus={statusFilterValue}
          key={`filter-box-${JSON.stringify(primaryFilterValue)}-${this.state.isTop5Mode}`}
        />
        
        {/* Inhaltsbereich */}
        <div className="list-widget__content">
          {loading ? (
            <div className="list-widget__loading">Daten werden geladen...</div>
          ) : error ? (
            <div className="list-widget__error">{error}</div>
          ) : displayData.length > 0 ? (
            <table className="list-widget__table">
              <thead>
                <tr>
                  <th>{columnHeaderLabel}</th>
                  <th>Anzahl</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.label}</td>
                    <td>{item.count}</td>
                    <td>
                      <span 
                        className="list-widget__status-indicator"
                        style={{ backgroundColor: this.getStatusColor(item.status) }}
                        title={item.status}
                      ></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="list-widget__no-data">
              Keine Daten verfügbar
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ListWidget;
